import { ApiPaths, api } from "@/lib/api";

import type {
  AuditSessionsPage,
  EventsPage,
  VerifyResponse,
} from "./types";


export async function fetchVerify(sessionId: string): Promise<VerifyResponse> {
  const res = await api.get<VerifyResponse>(ApiPaths.sessionAuditVerify(sessionId));
  return res.data;
}


export async function fetchEvents(
  sessionId: string,
  opts: { afterSeq?: number; limit?: number } = {},
): Promise<EventsPage> {
  const params: Record<string, string> = {};
  if (opts.afterSeq !== undefined) params.after_seq = String(opts.afterSeq);
  if (opts.limit !== undefined) params.limit = String(opts.limit);
  const res = await api.get<EventsPage>(ApiPaths.sessionAuditEvents(sessionId), {
    params,
  });
  return res.data;
}


export async function fetchAuditSessions(
  opts: {
    status?: string;
    since?: string;
    until?: string;
    beforeCreatedAt?: string;
    limit?: number;
  } = {},
): Promise<AuditSessionsPage> {
  const params: Record<string, string> = {};
  if (opts.status) params.status = opts.status;
  if (opts.since) params.since = opts.since;
  if (opts.until) params.until = opts.until;
  if (opts.beforeCreatedAt) params.before_created_at = opts.beforeCreatedAt;
  if (opts.limit !== undefined) params.limit = String(opts.limit);
  const res = await api.get<AuditSessionsPage>(ApiPaths.auditSessions, {
    params,
  });
  return res.data;
}


/** Fetch the per-session compliance PDF + trigger a browser download.
 *  Authenticated via the api client (JWT attached automatically) — a
 *  plain anchor link wouldn't carry the Bearer header. */
export async function downloadSessionAuditReportPdf(sessionId: string): Promise<void> {
  const res = await api.get<Blob>(ApiPaths.sessionAuditReportPdf(sessionId), {
    responseType: "blob",
  });
  const blob = res.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-${sessionId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


/** Result envelope so the caller can render the right inline error
 *  for the two known failure modes — KYB-not-verified (409) and
 *  signing-service-unreachable (502). */
export type SignedReportResult =
  | { ok: true }
  | {
      ok: false;
      reason: "kyc_required" | "signing_unavailable" | "unknown";
      detail?: string;
    };


/** Same shape as the unsigned variant, but hits the PAdES-signed
 *  endpoint. Backend gates the call on `kyc_status="verified"`; a 409
 *  with `reason="kyc_required"` is the expected ungated response. A
 *  502 means DataSure (or the local pyhanko fallback chain) is
 *  unreachable — the caller should suggest the unsigned variant. */
export async function downloadSessionAuditSignedReportPdf(
  sessionId: string,
): Promise<SignedReportResult> {
  try {
    const res = await api.get<Blob>(
      ApiPaths.sessionAuditSignedReportPdf(sessionId),
      { responseType: "blob" },
    );
    const blob = res.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-${sessionId}.signed.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (e) {
    const e2 = e as {
      response?: {
        status?: number;
        data?: { detail?: string; reason?: string };
      };
    };
    const st = e2?.response?.status;
    if (st === 409 && e2.response?.data?.reason === "kyc_required") {
      return {
        ok: false,
        reason: "kyc_required",
        detail:
          e2.response.data.detail
          || "KYB verification required to sign reports.",
      };
    }
    if (st === 502) {
      return {
        ok: false,
        reason: "signing_unavailable",
        detail: "Signing service unavailable — try the unsigned variant.",
      };
    }
    return { ok: false, reason: "unknown" };
  }
}


/** Walk the whole chain by following the after_seq cursor. Bounded by a
 * page-count safety cap so a misbehaving backend can't lock the UI in a
 * fetch loop. */
export async function fetchAllEvents(
  sessionId: string,
  opts: { pageLimit?: number; maxPages?: number } = {},
): Promise<{ events: EventsPage["results"]; truncated: boolean }> {
  const pageLimit = opts.pageLimit ?? 200;
  const maxPages = opts.maxPages ?? 50;
  const all: EventsPage["results"] = [];
  let afterSeq: number | undefined;
  for (let i = 0; i < maxPages; i++) {
    const page = await fetchEvents(sessionId, { afterSeq, limit: pageLimit });
    all.push(...page.results);
    if (!page.has_more) return { events: all, truncated: false };
    afterSeq = page.next_after_seq;
  }
  return { events: all, truncated: true };
}
